import { ColumnType, TableFilter } from '../..';
import { ReactComponent as ClearIcon } from '../assets/clearBtn.svg';
import { useOutsideClick } from 'src/Hooks';
import { useState, useEffect, useLayoutEffect, useMemo, FC } from 'react';
import classNames from 'classnames';
import getFilterControl from './DefaultFilters';
import st from './TableFilter.module.scss';

interface IFilterProps {
	/** ИД колонки */
	columnId: string;
	/** Список всех типов колонок */
	columnTypes: ColumnType[];
	/** Объект фильтра */
	filter?: TableFilter;
}

type FilterValue = string | string[];

/** Компонента-контейнер для фильтра таблицы */
const Filter: FC<IFilterProps> = (props) => {
	const { columnId, columnTypes, filter } = props;

	if (!columnId) {
		throw new Error('Не Id колонки!');
	}
	if (!columnTypes || !columnTypes.length) {
		throw new Error('Типы колонок не определены!');
	}

	const wrapperRef = useOutsideClick<HTMLDivElement>(() => {
		return setIsShowModal(false);
	});
	const [isShowModal, setIsShowModal] = useState<boolean>(false);
	// Кэшируем поле значение
	const [value, setValue] = useState<FilterValue>('');
	// Счетчик для сброса поиска в выпадающем списке при очистке фильтра без закрытия модалки
	const [resetKey, setResetKey] = useState<number>(0);

	// const columnType = columnTypes.find( c => c.id === columnId );
	const columnType = useMemo(() => {
		let col = columnTypes.find((c) => {
			return c.id === columnId;
		});
		if (!col) {
			console.warn(
				`Колонка с ИД ${columnId} не найдена в списке колонок реестра(`,
			);
			col = {
				id: columnId,
				type: 'string',
			};
		}
		return col;
	}, [columnId, columnTypes]);
	const sType = columnType?.type?.toString() ?? 'string';

	// Обновляем кэш значения при изменениях в состоянии фильтров
	// или при открытии/закрытии ( для сброса введенного значения без применении фильтра )
	useEffect(() => {
		const val = getFilterValue();
		setValue(val);
	}, [filter?.state, isShowModal]);

	const onKeyUp = (e: KeyboardEvent) => {
		if (!isShowModal) {
			return;
		}
		if (e.code === 'Enter') {
			applyValueHandler(e);
		} else if (e.code === 'Escape') {
			cancelHandler(e);
		}
	};

	useLayoutEffect(() => {
		window.addEventListener('keyup', onKeyUp);
		return () => {
			window.removeEventListener('keyup', onKeyUp);
		};
		// eslint-disable-next-line
	}, [isShowModal, onKeyUp]);

	// Автоскролл к фильтру при его открытии
	useLayoutEffect(() => {
		if (!isShowModal) {
			return;
		}

		wrapperRef.current?.scrollIntoView({
			behavior: 'smooth',
			block: 'end',
			inline: 'nearest',
		});
	}, [isShowModal]);

	// Переводит фокус в поле открытого фильтра, включая условно отрисованные поля списков
	useLayoutEffect(() => {
		if (!isShowModal) {
			return;
		}

		const filterInput = wrapperRef.current?.querySelector<HTMLInputElement>(
			'input:not([readonly]):not([disabled])',
		);
		filterInput?.focus();
	}, [isShowModal]);

	/** Применение фильтра */
	const applyValueHandler = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent> | KeyboardEvent,
	) => {
		e.preventDefault();
		e.stopPropagation();
		setFilterValue(value);
		setIsShowModal(false);
	};

	/** Отмена ( закрытие модалки ) */
	const cancelHandler = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent> | KeyboardEvent,
	) => {
		e.preventDefault();
		e.stopPropagation();
		setIsShowModal(false);
	};

	/** Открытие модалки фильтра */
	const onOpenModalHandler = () => {
		setIsShowModal(true);
	};

	/** Вызывается при нажатии на крестик */
	const onClearOrCloseModalHandler = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (value) {
			setValue('');
			setResetKey((prev) => {
				return prev + 1;
			});
			return;
		}

		if (isShowModal) {
			setIsShowModal(false);
		} else {
			setFilterValue('');
		}
	};

	/** Возвращает текущее значение фильтра из состояния */
	const getFilterValue = (): FilterValue => {
		const emptyValue =
			columnType?.isBetween || columnType?.multiple ? [] : '';
		if (!filter || !filter.state || !filter.state.length) {
			return emptyValue;
		}
		const index = filter.state.findIndex((f) => {
			return f.id === columnId;
		});
		if (index !== -1) {
			return filter?.state[index].value as FilterValue;
		}
		return emptyValue;
	};

	/** Устанавливает значение фильтра в стэйт */
	const setFilterValue = (val: FilterValue) => {
		filter?.setState((prev) => {
			const newFilters = [...prev];
			const index = newFilters.findIndex((f) => {
				return f.id === columnId;
			});
			if (index !== -1) {
				newFilters[index].value = val;
			} else {
				newFilters.push({ id: columnId, value: val });
			}
			return newFilters;
		});
	};

	// получаем компоненту фильтра
	const Filter = getFilterControl(sType);

	// Определим пропсы для фильтра
	const filterProps = {
		columnId,
		columnType,
		isShowModal,
		setIsShowModal,
		value,
		setValue,
		cancelHandler,
		applyValueHandler,
	};

	// Отрисуем обертку с фильтром внутри
	const classes = classNames(st.filterWrapper, { [st.active]: isShowModal });

	return (
		<div
			id={'filter-' + columnId}
			ref={wrapperRef}
			className={classes}
			onClick={onOpenModalHandler}
		>
			<Filter key={resetKey} {...filterProps} />
			{(!isNullOrEmpty(value) || isShowModal) && (
				<ClearIcon
					className={st.clearIcon}
					onClick={onClearOrCloseModalHandler}
					width={28}
					height={28}
				/>
			)}
		</div>
	);
};

const isNullOrEmpty = (val: any): boolean => {
	if (val === undefined || val === null) {
		return true;
	} else if (Array.isArray(val)) {
		return !val.length;
	} else {
		return !val;
	}
};

export default Filter;
